import {proxy} from "valtio";

import {IValtioModel} from "@/shared/models/base-model.interface";
import {Field, type FieldInstance} from "@/shared/models/form/fields";
import {Select, type SelectInstance, type SelectOption} from "@/shared/models/form/select";

import {VideoModalModel, VideoModalModelInstance} from "./parts/video-modal.model";

function uniqueOptions(options: Array<SelectOption<string>>): Array<SelectOption<string>> {
    const seen = new Set<string>();
    return options.filter((o) => {
        if (!o.value || seen.has(o.value)) return false;
        seen.add(o.value);
        return true;
    });
}

export interface VideoItem {
    id: number;
    title: string;
    description: string;
    url: string;
    image: {src: string; alt: string};
    location: string;
    categories: ReadonlyArray<string>; // Changed to readonly for Valtio compatibility
    subject: string;
    duration: string;
    likes: number;
    dislikes?: number;
    date: string; // ISO datetime for sorting
}

class VideoLibraryModelClass implements IValtioModel {
    // Data
    allVideos: Array<VideoItem>;
    // Computed durations cache (by video url)
    videoDurationByUrl = new Map<string, string>();
    // Prevent duplicate concurrent loads
    private loadingDurationUrls = new Set<string>();
    // Init lifecycle flag
    private initialized = false;

    // Filters
    location: SelectInstance<string>;
    category: SelectInstance<string>;
    subject: SelectInstance<string>;
    search: FieldInstance;

    // Pagination
    pageSize: number;
    visibleCount: number;

    // Simple local counters for UI-only increments
    extraLikes = new Map<number, number>();
    extraDislikes = new Map<number, number>();
    uiVersion = 0;
    modalModel: VideoModalModelInstance;

    constructor(items: Array<VideoItem>, options?: {pageSize?: number}) {
        const pageSize = options?.pageSize ?? 12;
        this.pageSize = pageSize;
        this.visibleCount = Math.min(pageSize, items.length);

        // Sort items by date (newest first) before storing
        this.allVideos = [...items].sort((a, b) => {
            if (!a.date || !b.date) return 0;
            return new Date(b.date).getTime() - new Date(a.date).getTime();
        });

        this.modalModel = VideoModalModel();

        // Build filter options from refs (franchise → location, categories array, subject) – unique values
        const locationOptions = uniqueOptions(items.map((i) => ({value: i.location, label: i.location})));

        // Flatten all categories from all videos
        const allCategories = items.flatMap((i) => i.categories);
        const categoryOptions = uniqueOptions(allCategories.map((c) => ({value: c, label: c})));

        const subjectOptions = uniqueOptions(items.map((i) => ({value: i.subject, label: i.subject})));

        this.location = Select<string>({options: locationOptions, isClearable: true});
        this.category = Select<string>({options: categoryOptions, isClearable: true});
        this.subject = Select<string>({options: subjectOptions, isClearable: true});
        this.search = Field({initialValue: ""});
    }

    init(): void {
        this.initialized = true;
    }
    dispose(): void {
        this.initialized = false;
    }

    openVideoModal(item: VideoItem): void {
        this.modalModel.open({title: item.title, url: item.url});
    }

    /**
     * Returns a cached formatted duration for the given video url.
     * If not available yet, starts async metadata loading and returns an empty string for now.
     */
    getVideoDuration(url: string): string {
        const cached = this.videoDurationByUrl.get(url);
        if (cached) return cached;
        // Trigger background load if not already loading
        if (!this.loadingDurationUrls.has(url)) {
            void this.loadVideoDuration(url);
        }
        return "";
    }

    /**
     * Loads video metadata off-DOM and stores a formatted duration string.
     * Increments uiVersion to refresh any subscribers.
     */
    private async loadVideoDuration(url: string): Promise<void> {
        this.loadingDurationUrls.add(url);
        try {
            const videoEl = document.createElement("video");
            videoEl.preload = "metadata";
            videoEl.src = url;

            const durationSec: number = await new Promise((resolve, reject) => {
                const onLoaded = (): void => {
                    // Some browsers report NaN until readyState is enough; guard
                    const dur = Number.isFinite(videoEl.duration) ? videoEl.duration : 0;
                    resolve(dur);
                };
                const onError = (): void => {
                    reject(new Error("Failed to load video metadata"));
                };

                videoEl.addEventListener("loadedmetadata", onLoaded, {once: true});
                videoEl.addEventListener("error", onError, {once: true});
            });

            const formatted = this.formatDuration(Math.max(0, Math.round(durationSec)));
            this.videoDurationByUrl.set(url, formatted);
            this.uiVersion++;
        } catch {
            // Keep silent; do not block UI. Optionally fallback could be set here.
        } finally {
            this.loadingDurationUrls.delete(url);
        }
    }

    private formatDuration(totalSeconds: number): string {
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const mm = hours > 0 ? String(minutes).padStart(2, "0") : String(minutes);
        const ss = String(seconds).padStart(2, "0");
        return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
    }

    get filteredVideos(): Array<VideoItem> {
        const query = this.search.value.trim().toLowerCase();
        const location = this.location.selectedOption?.value;
        const category = this.category.selectedOption?.value;
        const subject = this.subject.selectedOption?.value;

        return this.allVideos.filter((v) => {
            if (location && v.location !== location) return false;
            // Check if selected category is in video's categories array
            if (category && !v.categories.includes(category)) return false;
            if (subject && v.subject !== subject) return false;
            return !(query && !`${v.title} ${v.description}`.toLowerCase().includes(query));
        });
    }

    get visibleVideos(): Array<VideoItem> {
        return this.filteredVideos.slice(0, this.visibleCount);
    }

    canLoadMore(): boolean {
        return this.visibleCount < this.filteredVideos.length;
    }

    loadMore(): void {
        if (this.canLoadMore()) {
            this.visibleCount = Math.min(this.visibleCount + this.pageSize, this.filteredVideos.length);
        }
    }

    currentLikes(id: number): number {
        const base = this.allVideos.find((v) => v.id === id)?.likes ?? 0;
        const delta = this.extraLikes.get(id) ?? 0;
        return base + delta;
    }

    currentDislikes(id: number): number {
        const base = 0; // UI-only for now
        const delta = this.extraDislikes.get(id) ?? 0;
        return base + delta;
    }

    incrementLike(id: number): void {
        const next = (this.extraLikes.get(id) ?? 0) + 1;
        this.extraLikes.set(id, next);
        this.uiVersion++;
    }

    incrementDislike(id: number): void {
        const next = (this.extraDislikes.get(id) ?? 0) + 1;
        this.extraDislikes.set(id, next);
        this.uiVersion++;
    }
}

export type VideoLibraryModelInstance = VideoLibraryModelClass;

/**
 * Create video library model. Pass items for flexible/API data.
 */
export function VideoLibraryModel(initialItems: Array<VideoItem>, options?: {pageSize?: number}): VideoLibraryModelInstance {
    return proxy(new VideoLibraryModelClass(initialItems, options));
}
